import { useContext, useEffect } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRepeat } from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Button, Image, FileInput, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProvider, { useProviderForm } from '@hooks/useProvider'

const defaultState = {
  showImage: false,
  type: '',
}

const UploadProviderLogoModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, updateProvider } = callbacks || {}

  const [state, setState] = useSetState(defaultState)
  const { showImage, type } = state

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { provider, type: initType } = modalPayload

  const alternateLogoThumb = digObject(provider, 'alternate_logo.thumb')
  const logoThumb = digObject(provider, 'logo.thumb')

  useEffect(() => setState({ type: initType }), [initType])

  useEffect(() => {
    if ((type === 'logo' && !!logoThumb) || (type === 'alternate_logo' && !!alternateLogoThumb)){
      setState({ showImage: true })
    } else {
      setState({ showImage: false })
    }
  }, [provider.id, type])

  const {
    entityState,
    setEntityState,
  } = useProviderForm(provider, { validateOn: 'change' })

  const {
    callbacks: { updateProvider: updateFn },
    updating,
  } = useProvider(provider)

  const updateProviderPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_provider_logo: true,
      include_provider_alternate_logo: true,
    },
    toastText: 'Logo Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Upload" titleSecondLine="Logos" />

      <SidebarModal.Content>
        <Form>
          <Form.Field direction="column" label="* Type" marginTop="large" name="logo">
            <select onChange={e => setState({ type: e.target.value })} value={type}>
              <option value="">Please Select...</option>
              <option value="logo">Logo (400x400 PNG)</option>
              <option value="alternate_logo">Alternate Logo (800x480 PNG)</option>
            </select>
          </Form.Field>

          {!showImage && (
            <Form.Field label="* Upload Logos" marginTop="large">
              <FileInput
                input={{
                  onChange: (fileData, fileObject) => (
                    setEntityState({ [type]: fileData, [`${type}_file_name`]: fileObject.name })
                  ),
                }}
              />
            </Form.Field>
          )}

          {showImage && (
            <>
              <Form.Field label="Image" marginTop="large">
                <Box justifyContent="center">
                  <Image
                    backgroundSize="contain"
                    borderRadius={5}
                    boxProps={{ border: '1px solid', borderColor: 'lineColor' }}
                    height={200}
                    url={type === 'logo' ? logoThumb : alternateLogoThumb}
                    width={200}
                  />
                </Box>
              </Form.Field>

              <Form.Field label="More Options" marginTop="large">
                <Button
                  onClick={() => setState({ showImage: false })}
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faRepeat} />}
                  size="medium"
                >
                  Replace Image
                </Button>
              </Form.Field>
            </>
          )}
        </Form>

      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!type}
          loading={updating}
          onClick={() => updateProvider(updateProviderPayload)}
          size="large"
        >
          Save Logo
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

UploadProviderLogoModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

UploadProviderLogoModal.defaultProps = {
  modalKey: 'UploadProviderLogoModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <UploadProviderLogoModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
